import { useEffect, useMemo, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Edit, Bell, CheckSquare, Square, AlertCircle, Clock, ListTodo, RefreshCw, ArrowRight, Repeat, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageLoader } from "@/components/Spinner";
import { EmptyState } from "@/components/EmptyState";
import { useSubscription } from "@/lib/subscription";

const URGENCIA_META = {
  alta: { label: "Alta", color: "text-red-700 bg-red-50 border-red-200", dot: "bg-red-500" },
  media: { label: "Media", color: "text-amber-700 bg-amber-50 border-amber-200", dot: "bg-amber-500" },
  baja: { label: "Baja", color: "text-blue-700 bg-blue-50 border-blue-200", dot: "bg-blue-500" },
};

const ESTADO_LABEL = { pendiente: "Pendiente", en_progreso: "En progreso", completada: "Completada" };

const REPETIR_LABEL = {
  ninguna: "No repetir",
  diaria: "Diaria",
  semanal: "Semanal",
  quincenal: "Quincenal",
  mensual: "Mensual",
  anual: "Anual",
  fechas_especificas: "Fechas específicas",
};

const DIAS_SEMANA = [
  { i: 0, short: "L", label: "Lun" },
  { i: 1, short: "M", label: "Mar" },
  { i: 2, short: "X", label: "Mié" },
  { i: 3, short: "J", label: "Jue" },
  { i: 4, short: "V", label: "Vie" },
  { i: 5, short: "S", label: "Sáb" },
  { i: 6, short: "D", label: "Dom" },
];

const emptyForm = () => ({
  titulo: "",
  descripcion: "",
  urgencia: "media",
  estado: "pendiente",
  fecha_vencimiento: "",
  hora_vencimiento: "",
  recordatorio_at: "",
  asignado_a: "",
  // Recurrence (Pro feature)
  repetir: "ninguna",
  todo_el_dia: true,
  repetir_hora: "09:00",
  repetir_dias_semana: [],
  repetir_dia_mes: 1,
  repetir_mes_dia: "",
  repetir_desde: "",
  repetir_fechas: [],
});

// Combine YYYY-MM-DD + HH:MM (treated as user's local time) into an ISO UTC string.
const toIsoLocal = (date, time) => {
  if (!date) return "";
  const t = time || "09:00";
  const local = new Date(`${date}T${t}`);
  if (isNaN(local.getTime())) return "";
  return local.toISOString();
};

const fromIso = (iso) => {
  if (!iso) return { date: "", time: "" };
  const d = new Date(iso);
  if (isNaN(d.getTime())) return { date: "", time: "" };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
};

export default function Tasks() {
  const { hasFeature } = useSubscription();
  const canRecur = hasFeature("recurring_tasks");
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [reminderDate, setReminderDate] = useState("");
  const [reminderTime, setReminderTime] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterUrgencia, setFilterUrgencia] = useState("__all__");
  const [filterEstado, setFilterEstado] = useState("__active__");

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await api.get("/tasks");
      setTasks(res.data);
    } catch {
      toast.error("Error al cargar tareas");
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      if (filterUrgencia !== "__all__" && t.urgencia !== filterUrgencia) return false;
      if (filterEstado === "__active__" && t.estado === "completada") return false;
      if (filterEstado === "__all__") { /* no filter */ }
      else if (filterEstado !== "__active__" && t.estado !== filterEstado) return false;
      return true;
    }).sort((a, b) => {
      // pending first, then by urgency (alta>media>baja), then by due date
      const estadoOrd = { pendiente: 0, en_progreso: 1, completada: 2 };
      const urgOrd = { alta: 0, media: 1, baja: 2 };
      if (estadoOrd[a.estado] !== estadoOrd[b.estado]) return estadoOrd[a.estado] - estadoOrd[b.estado];
      if (urgOrd[a.urgencia] !== urgOrd[b.urgencia]) return urgOrd[a.urgencia] - urgOrd[b.urgencia];
      const aDate = a.fecha_vencimiento || "9999-12-31";
      const bDate = b.fecha_vencimiento || "9999-12-31";
      return aDate.localeCompare(bDate);
    });
  }, [tasks, filterUrgencia, filterEstado]);

  const stats = useMemo(() => {
    const pendientes = tasks.filter(t => t.estado !== "completada").length;
    const altas = tasks.filter(t => t.urgencia === "alta" && t.estado !== "completada").length;
    const hoy = new Date().toISOString().split("T")[0];
    const vencen_hoy = tasks.filter(t => t.fecha_vencimiento === hoy && t.estado !== "completada").length;
    const vencidas = tasks.filter(t => t.fecha_vencimiento && t.fecha_vencimiento < hoy && t.estado !== "completada").length;
    return { pendientes, altas, vencen_hoy, vencidas };
  }, [tasks]);

  const openNew = () => {
    setEditing(null);
    setFormData(emptyForm());
    setReminderDate("");
    setReminderTime("");
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setFormData({
      titulo: t.titulo,
      descripcion: t.descripcion || "",
      urgencia: t.urgencia,
      estado: t.estado,
      fecha_vencimiento: t.fecha_vencimiento || "",
      hora_vencimiento: t.hora_vencimiento || "",
      recordatorio_at: t.recordatorio_at || "",
      asignado_a: t.asignado_a || "",
      repetir: t.repetir || "ninguna",
      todo_el_dia: t.todo_el_dia !== undefined ? !!t.todo_el_dia : true,
      repetir_hora: t.repetir_hora || "09:00",
      repetir_dias_semana: t.repetir_dias_semana || [],
      repetir_dia_mes: t.repetir_dia_mes || 1,
      repetir_mes_dia: t.repetir_mes_dia || "",
      repetir_desde: t.repetir_desde || "",
      repetir_fechas: t.repetir_fechas || [],
    });
    const r = fromIso(t.recordatorio_at);
    setReminderDate(r.date);
    setReminderTime(r.time);
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!formData.titulo.trim()) {
      toast.error("El título es obligatorio");
      return;
    }
    const recordatorio_at = reminderDate ? toIsoLocal(reminderDate, reminderTime) : "";
    // If the user changed the reminder timestamp, reset the "recordado" flag
    // so the new alert fires.
    const recordado = editing && editing.recordatorio_at === recordatorio_at ? editing.recordado : false;
    // Defensive: ensure recurrence config is consistent with the selected pattern.
    const repetir = canRecur ? formData.repetir : "ninguna";
    const payload = {
      ...formData,
      recordatorio_at,
      recordado,
      repetir,
      repetir_dia_mes: parseInt(formData.repetir_dia_mes || 0, 10) || 0,
    };
    try {
      if (editing) {
        await api.put(`/tasks/${editing.id}`, { ...payload, id: editing.id });
        toast.success("Tarea actualizada");
      } else {
        await api.post("/tasks", payload);
        toast.success("Tarea creada");
      }
      setDialogOpen(false);
      setEditing(null);
      fetchTasks();
    } catch (err) {
      const det = err?.response?.data?.detail;
      if (det?.error === "plan_required") {
        toast.error("Tareas recurrentes requieren plan Pro");
      } else {
        toast.error(typeof det === "string" ? det : "Error al guardar la tarea");
      }
    }
  };

  const toggleComplete = async (t) => {
    try {
      const nextEstado = t.estado === "completada" ? "pendiente" : "completada";
      await api.put(`/tasks/${t.id}`, { ...t, estado: nextEstado });
      toast.success(nextEstado === "completada" ? "Tarea completada" : "Tarea reabierta");
      fetchTasks();
    } catch {
      toast.error("Error al actualizar el estado");
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/tasks/${deleteTarget.id}`);
      toast.success("Tarea eliminada");
      setDeleteTarget(null);
      fetchTasks();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <PageLoader label="Cargando tareas…" testid="tasks-loader" />;

  return (
    <div data-testid="tasks-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
            Tareas
          </h2>
          <p className="text-slate-500 text-sm mt-1">Gestiona tus pendientes con niveles de urgencia, fechas y recordatorios</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="btn-primary rounded-sm" data-testid="new-task-button">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Tarea
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Tarea" : "Nueva Tarea"}</DialogTitle>
            </DialogHeader>            <div className="space-y-4 mt-3">
              <div>
                <label className="block text-sm font-medium mb-1.5">Título *</label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                  className="rounded-sm"
                  placeholder="Ej: Llamar al proveedor por la entrega"
                  data-testid="task-title-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Descripción</label>
                <Textarea
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                  className="rounded-sm"
                  rows={3}
                  placeholder="Detalles adicionales..."
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Urgencia</label>
                  <Select value={formData.urgencia} onValueChange={(v) => setFormData({ ...formData, urgencia: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="task-urgency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="alta">🔴 Alta</SelectItem>
                      <SelectItem value="media">🟡 Media</SelectItem>
                      <SelectItem value="baja">🔵 Baja</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Estado</label>
                  <Select value={formData.estado} onValueChange={(v) => setFormData({ ...formData, estado: v })}>
                    <SelectTrigger className="rounded-sm" data-testid="task-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_progreso">En progreso</SelectItem>
                      <SelectItem value="completada">Completada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Fecha de vencimiento</label>
                  <Input
                    type="date"
                    value={formData.fecha_vencimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_vencimiento: e.target.value })}
                    className="rounded-sm"
                    data-testid="task-duedate-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Hora de vencimiento</label>
                  <Input
                    type="time"
                    value={formData.hora_vencimiento}
                    onChange={(e) => setFormData({ ...formData, hora_vencimiento: e.target.value })}
                    className="rounded-sm"
                    data-testid="task-duetime-input"
                  />
                </div>
              </div>

              <div className="border border-blue-200 bg-blue-50 rounded-sm p-3">
                <div className="flex items-center gap-2 mb-2">
                  <Bell className="h-4 w-4 text-[rgb(0,47,167)]" />
                  <h4 className="text-sm font-semibold text-[rgb(0,47,167)]">Alerta / Recordatorio</h4>
                </div>
                <p className="text-xs text-slate-600 mb-2">
                  Te avisaremos con un toast y una notificación del navegador cuando se cumpla esta fecha/hora.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-600">Fecha</label>
                    <Input
                      type="date"
                      value={reminderDate}
                      onChange={(e) => setReminderDate(e.target.value)}
                      className="rounded-sm h-9 text-xs bg-white"
                      data-testid="task-reminder-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-slate-600">Hora</label>
                    <Input
                      type="time"
                      value={reminderTime}
                      onChange={(e) => setReminderTime(e.target.value)}
                      className="rounded-sm h-9 text-xs bg-white"
                      data-testid="task-reminder-time-input"
                    />
                  </div>
                </div>
                {reminderDate && (
                  <button
                    type="button"
                    onClick={() => { setReminderDate(""); setReminderTime(""); }}
                    className="text-[11px] text-red-600 mt-2 hover:underline"
                  >
                    Quitar recordatorio
                  </button>
                )}
              </div>

              {/* Recurrencia (Pro) */}
              <div className={`border rounded-sm p-3 ${canRecur ? "border-violet-200 bg-violet-50" : "border-slate-200 bg-slate-50"}`} data-testid="task-recurrence-section">
                <div className="flex items-center gap-2 mb-2">
                  <Repeat className={`h-4 w-4 ${canRecur ? "text-violet-700" : "text-slate-500"}`} />
                  <h4 className={`text-sm font-semibold ${canRecur ? "text-violet-900" : "text-slate-700"}`}>
                    Repetir tarea
                  </h4>
                  {!canRecur && (
                    <Link
                      to="/upgrade?reason=recurring_tasks"
                      className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200"
                      data-testid="task-recurrence-upgrade"
                    >
                      <Crown className="h-3 w-3" />
                      Pro
                    </Link>
                  )}
                </div>
                {!canRecur && (
                  <p className="text-[11px] text-slate-500 italic mb-2">
                    Disponible en plan Pro. Las tareas se generan automáticamente según el patrón configurado (diaria, semanal con días específicos, mensual, anual).
                  </p>
                )}
                <Select
                  value={formData.repetir}
                  onValueChange={(v) => setFormData({ ...formData, repetir: v })}
                  disabled={!canRecur}
                >
                  <SelectTrigger className="rounded-sm bg-white" data-testid="task-repeat-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(REPETIR_LABEL).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {canRecur && formData.repetir === "semanal" && (
                  <div className="mt-2" data-testid="task-repeat-weekly-days">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-1">Días de la semana</label>
                    <div className="flex gap-1 flex-wrap">
                      {DIAS_SEMANA.map(d => {
                        const checked = (formData.repetir_dias_semana || []).includes(d.i);
                        return (
                          <button
                            key={d.i}
                            type="button"
                            onClick={() => {
                              const cur = new Set(formData.repetir_dias_semana || []);
                              if (cur.has(d.i)) cur.delete(d.i); else cur.add(d.i);
                              setFormData({ ...formData, repetir_dias_semana: [...cur].sort() });
                            }}
                            className={`h-8 w-8 rounded-sm text-xs font-bold border transition-colors ${checked ? "bg-violet-600 text-white border-violet-700" : "bg-white text-slate-600 border-slate-300 hover:border-violet-400"}`}
                            data-testid={`task-repeat-day-${d.i}`}
                            title={d.label}
                          >
                            {d.short}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {canRecur && formData.repetir === "mensual" && (
                  <div className="mt-2" data-testid="task-repeat-monthly-day">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-1">Día del mes (1–31)</label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.repetir_dia_mes}
                      onChange={(e) => setFormData({ ...formData, repetir_dia_mes: parseInt(e.target.value || 1, 10) })}
                      className="rounded-sm h-9 bg-white w-24"
                      data-testid="task-repeat-monthly-input"
                    />
                    <p className="text-[10px] text-slate-500 italic mt-1">
                      Si el mes no tiene el día (ej: 31 en febrero), se ajusta al último día.
                    </p>
                  </div>
                )}

                {canRecur && formData.repetir === "anual" && (
                  <div className="mt-2" data-testid="task-repeat-yearly-date">
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-1">Mes-día (MM-DD)</label>
                    <Input
                      type="text"
                      pattern="\d{2}-\d{2}"
                      placeholder="12-25"
                      value={formData.repetir_mes_dia}
                      onChange={(e) => setFormData({ ...formData, repetir_mes_dia: e.target.value })}
                      className="rounded-sm h-9 bg-white w-32 mono"
                      data-testid="task-repeat-yearly-input"
                    />
                    <p className="text-[10px] text-slate-500 italic mt-1">
                      Ejemplo: <strong>12-25</strong> para Navidad, <strong>06-15</strong> para mitad de año.
                    </p>
                  </div>
                )}

                {canRecur && formData.repetir !== "ninguna" && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <label className="flex items-center gap-1.5 text-xs text-slate-700">
                      <Checkbox
                        checked={!!formData.todo_el_dia}
                        onCheckedChange={(v) => setFormData({ ...formData, todo_el_dia: !!v })}
                        data-testid="task-repeat-allday"
                      />
                      Todo el día
                    </label>
                    {!formData.todo_el_dia && (
                      <Input
                        type="time"
                        value={formData.repetir_hora}
                        onChange={(e) => setFormData({ ...formData, repetir_hora: e.target.value })}
                        className="rounded-sm h-8 bg-white text-xs"
                        data-testid="task-repeat-time"
                      />
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1.5">Asignado a (opcional)</label>
                <Input
                  value={formData.asignado_a}
                  onChange={(e) => setFormData({ ...formData, asignado_a: e.target.value })}
                  className="rounded-sm"
                  placeholder="Nombre del responsable"
                  data-testid="task-assignee-input"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
                <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancelar</Button>
                <Button onClick={submit} className="btn-primary rounded-sm" data-testid="save-task-button">
                  {editing ? "Actualizar" : "Crear Tarea"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pagos Recurrentes — shortcut to Gastos filtered by repetir_mensual */}
      <Link
        to="/expenses"
        state={{ filterRecurring: true }}
        className="block mb-5 group"
        data-testid="recurring-payments-shortcut"
      >
        <Card className="border border-indigo-200 bg-gradient-to-r from-indigo-50 to-violet-50 rounded-sm p-4 hover:shadow-sm transition-shadow">
          <div className="flex items-center gap-4">
            <div className="p-2.5 rounded-sm bg-white border border-indigo-200">
              <RefreshCw className="h-5 w-5 text-indigo-700" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                  Pagos Recurrentes
                </h3>
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-800">
                  Mensuales
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-0.5">
                Alquiler, internet, salarios y demás gastos fijos — administralos en un solo lugar con fechas fijas.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-indigo-700 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </div>
        </Card>
      </Link>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-5">
        <Card className="bg-white border border-slate-200 rounded-sm p-3" data-testid="stat-pending">
          <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wider">
            <Clock className="h-3.5 w-3.5" /> Pendientes
          </div>
          <div className="text-2xl font-bold mt-1">{stats.pendientes}</div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-3">
          <div className="flex items-center gap-2 text-xs text-red-600 uppercase tracking-wider">
            <AlertCircle className="h-3.5 w-3.5" /> Urgencia alta
          </div>
          <div className="text-2xl font-bold mt-1">{stats.altas}</div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-xs text-amber-700 uppercase tracking-wider">Vencen hoy</div>
          <div className="text-2xl font-bold mt-1">{stats.vencen_hoy}</div>
        </Card>
        <Card className="bg-white border border-slate-200 rounded-sm p-3">
          <div className="text-xs text-red-700 uppercase tracking-wider">Vencidas</div>
          <div className="text-2xl font-bold mt-1">{stats.vencidas}</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border border-slate-200 rounded-sm p-3 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs font-medium text-slate-600 uppercase tracking-wider">Filtros:</span>
          <Select value={filterEstado} onValueChange={setFilterEstado}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-44" data-testid="task-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__active__">Activas</SelectItem>
              <SelectItem value="__all__">Todas</SelectItem>
              <SelectItem value="pendiente">Pendiente</SelectItem>
              <SelectItem value="en_progreso">En progreso</SelectItem>
              <SelectItem value="completada">Completadas</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterUrgencia} onValueChange={setFilterUrgencia}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-44" data-testid="task-filter-urgency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Toda urgencia</SelectItem>
              <SelectItem value="alta">🔴 Alta</SelectItem>
              <SelectItem value="media">🟡 Media</SelectItem>
              <SelectItem value="baja">🔵 Baja</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-slate-500 ml-auto">{filteredTasks.length} tarea{filteredTasks.length === 1 ? "" : "s"}</span>
        </div>
      </Card>

      {/* List */}
      {filteredTasks.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title={tasks.length === 0 ? "Aún no hay tareas" : "Sin coincidencias"}
          description={
            tasks.length === 0
              ? "Organizá tus pendientes con urgencia, fechas de vencimiento y recordatorios automáticos."
              : "No encontramos tareas con esos filtros. Probá ajustar la búsqueda o estado."
          }
          actionLabel={tasks.length === 0 ? "Nueva tarea" : undefined}
          actionOnClick={tasks.length === 0 ? openNew : undefined}
          testid="tasks-empty-state"
        />
      ) : (
        <Card className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="divide-y divide-slate-100">
            {filteredTasks.map(t => {
              const urg = URGENCIA_META[t.urgencia] || URGENCIA_META.media;
              const isCompleted = t.estado === "completada";
              const today = new Date().toISOString().split("T")[0];
              const isOverdue = t.fecha_vencimiento && t.fecha_vencimiento < today && !isCompleted;
              return (
                <div
                  key={t.id}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${isCompleted ? "opacity-60" : ""}`}
                  data-testid="task-row"
                >
                  <button
                    type="button"
                    onClick={() => toggleComplete(t)}
                    className="mt-0.5 flex-shrink-0"
                    title={isCompleted ? "Reabrir" : "Marcar completada"}
                    data-testid={`task-toggle-${t.id}`}
                  >
                    {isCompleted
                      ? <CheckSquare className="h-5 w-5 text-emerald-600" />
                      : <Square className="h-5 w-5 text-slate-400 hover:text-slate-700" />}
                  </button>

                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => openEdit(t)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block w-2 h-2 rounded-full ${urg.dot}`} />
                      <span className={`text-sm font-medium ${isCompleted ? "line-through text-slate-500" : "text-slate-900"}`}>
                        {t.titulo}
                      </span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm ${urg.color}`}>
                        {urg.label}
                      </span>
                      {t.estado === "en_progreso" && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm text-blue-700 bg-blue-50 border-blue-200">
                          {ESTADO_LABEL.en_progreso}
                        </span>
                      )}
                      {isOverdue && (
                        <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm text-red-700 bg-red-50 border-red-200">
                          Vencida
                        </span>
                      )}
                      {t.repetir && t.repetir !== "ninguna" && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border rounded-sm text-violet-700 bg-violet-50 border-violet-200"
                          title={`Repite: ${REPETIR_LABEL[t.repetir] || t.repetir}`}
                          data-testid={`task-recurrence-badge-${t.id}`}
                        >
                          <Repeat className="h-2.5 w-2.5" />
                          {REPETIR_LABEL[t.repetir] || t.repetir}
                        </span>
                      )}
                    </div>
                    {t.descripcion && (
                      <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{t.descripcion}</div>
                    )}
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1.5 flex-wrap">
                      {t.fecha_vencimiento && (
                        <span className={isOverdue ? "text-red-600 font-medium" : ""}>
                          📅 {t.fecha_vencimiento}{t.hora_vencimiento ? ` ${t.hora_vencimiento}` : ""}
                        </span>
                      )}
                      {t.recordatorio_at && (
                        <span className="flex items-center gap-1">
                          <Bell className="h-3 w-3" />
                          {new Date(t.recordatorio_at).toLocaleString("es-CR", { dateStyle: "short", timeStyle: "short" })}
                        </span>
                      )}
                      {t.asignado_a && <span>👤 {t.asignado_a}</span>}
                    </div>
                  </div>

                  <div className="flex gap-1 flex-shrink-0">
                    <Button onClick={() => openEdit(t)} variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm">
                      <Edit className="h-3.5 w-3.5" />
                    </Button>
                    <Button onClick={() => setDeleteTarget(t)} variant="ghost" size="sm" className="h-8 w-8 p-0 rounded-sm text-red-600 hover:bg-red-50" data-testid={`delete-task-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar tarea?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar "{deleteTarget?.titulo}". Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
